import {h, Fragment} from "preact"
import { useProductFilters } from "@nosto/preact";
import { useEffect, useMemo, useState } from "preact/hooks";
import { PRODUCT_TYPE_MAPPING, PRODUCT_TYPE_FILTERS } from "../config"

const CLASS = "class";
const ATTRIBUTES = "attributes";
const TYPE = "Type";

const getModalOptions = (updatedTypeFilters) => updatedTypeFilters.reduce((filtersHtml, { value }) =>
    filtersHtml + `<div class="options" data-value="${value}">${PRODUCT_TYPE_MAPPING[value]}</div>`, ""
);

const getFilterInnerHTML = (previousAppliedFilter, updatedTypeFilters) =>
    `<div class="custom-filter-modal relative">
    <div id="filter-label" aria-expanded="false" class="flex items-center">
        <span class="flex-1 truncate">${PRODUCT_TYPE_MAPPING[previousAppliedFilter] || "Top Results"}</span>
        <svg width="10" height="6" viewBox="0 0 10 7" fill="none" xmlns="http://www.w3.org/2000/svg" class="custom-filter-svg duration-300">
            <path fill-rule="evenodd" clip-rule="evenodd"
                d="M0.292893 0.792893C0.683416 0.402369 1.31658 0.402369 1.7071 0.792893L4.99999 4.08579L8.29288 0.792893C8.6834 0.402369 9.31657 0.402369 9.70709 0.792893C10.0976 1.18342 10.0976 1.81658 9.70709 2.20711L5.7071 6.20711C5.31657 6.59763 4.68341 6.59763 4.29289 6.20711L0.292893 2.20711C-0.0976309 1.81658 -0.0976309 1.18342 0.292893 0.792893Z"
                fill="#707073"/>
        </svg>
    </div>
    <div class="filter-custom-dropdown hidden absolute border bg-white w-full">
        <div class="options" data-value="">Top Results</div>
        ${getModalOptions(updatedTypeFilters)}
    </div>
</div>`

export default ({ selectedFilters, updateProductSearch, searchQuery, updatedTypeFilters, productTypeFilter, handleProductTypeFilterUpdate, showProductFilterOnCollection }) => {
    const { filters } = useProductFilters();
    const [isCustomFilterDropdownOpen, setIsCustomFilterDropdownOpen] = useState(false);
    const previousAppliedFilter = useMemo(() => filters.find(({ name }) => name === TYPE)?.value || "", [filters]);

    useEffect(() => {
        if (!searchQuery) return
        const typeFilterEl = document.getElementById("product-type-filter");
        if (!typeFilterEl) return;

        const searchBoxEl = document.querySelector("#search-form .search__input");
        const clearButtonEl = document.getElementById("reset-search");
        if (!updatedTypeFilters || updatedTypeFilters?.length <= 1) {
            typeFilterEl.innerHTML = "";
            searchBoxEl.classList.remove("rounded-r-0");
            clearButtonEl.classList.remove("clear-button-right");
            return;
        }

        typeFilterEl.innerHTML = getFilterInnerHTML(previousAppliedFilter, updatedTypeFilters)

        if (searchBoxEl) searchBoxEl.classList.add("rounded-r-0");

        if (clearButtonEl) clearButtonEl.classList.add("clear-button-right");

        const filterLabel = document.querySelector("#product-type-filter #filter-label");
        const filterModel = document.querySelector("#product-type-filter .filter-custom-dropdown");
        const updateModalVisibility = () => {
            const isModelClosed = filterModel.classList.contains("hidden");
            isModelClosed ? filterModel.classList.remove("hidden") : filterModel.classList.add("hidden");
            setIsCustomFilterDropdownOpen(isModelClosed);
        };

        filterLabel.addEventListener("click", updateModalVisibility);

        const targetNode = document.getElementById("searchpage-nosto-results");
        let observer
        if (targetNode) {
            observer = new MutationObserver(function (mutationsList) {
                mutationsList.forEach(function (mutation) {
                    if (mutation.type === ATTRIBUTES && mutation.attributeName === CLASS) {
                        const isFiltersHidden = targetNode.classList.value.includes("hidden")
                        if (isFiltersHidden) {
                            searchBoxEl.classList.remove("rounded-r-0");
                            typeFilterEl.innerHTML = "";
                        }
                    }
                });
            });

            const config = { attributes: true };
            observer.observe(targetNode, config);
        }

        return () => {
            filterLabel.removeEventListener("click", updateModalVisibility);
            if (observer) observer.disconnect()
            typeFilterEl.innerHTML = "";
            searchBoxEl.classList.remove("rounded-r-0");
            clearButtonEl.classList.remove("clear-button-right");
        };
    }, [previousAppliedFilter, updatedTypeFilters]);

    useEffect(() => {
        if (!isCustomFilterDropdownOpen) return;
        const filterModel = document.querySelector("#product-type-filter .filter-custom-dropdown");
        const handleClickOutside = (e) => {
            if (filterModel.contains(e.target)) return;
            setIsCustomFilterDropdownOpen(false);
            filterModel.classList.add("hidden");
        };

        window.addEventListener("click", handleClickOutside);

        return () => {
            window.removeEventListener("click", handleClickOutside);
        };
    }, [isCustomFilterDropdownOpen]);

    useEffect(() => {
        if (!isCustomFilterDropdownOpen) return;
        const filterModel = document.querySelector("#product-type-filter .filter-custom-dropdown");
        const handleFilterSelect = (e) => {
            const selectedFilter = e.target.getAttribute("data-value");
            if (selectedFilter === previousAppliedFilter) return;

            let updatedFilters = selectedFilters
            if (!selectedFilter) updatedFilters = updatedFilters.filter(({ name }) => name !== TYPE)
            else if (previousAppliedFilter) updatedFilters = updatedFilters.map((filter) => filter.name !== TYPE ? filter : { name: TYPE, field: productTypeFilter.field, value: selectedFilter })
            else updatedFilters = [...updatedFilters, { name: TYPE, field: productTypeFilter.field, value: selectedFilter }]

            updateProductSearch(updatedFilters)
            filterModel.classList.add("hidden");
            setIsCustomFilterDropdownOpen(false);
        };

        const filtersEl = document.querySelectorAll("#product-type-filter .filter-custom-dropdown .options");
        filtersEl.forEach((filterEl) => {
            filterEl.addEventListener("click", handleFilterSelect);
        });

        return () => {
            filtersEl.forEach((filterEl) => {
                filterEl.removeEventListener("click", handleFilterSelect);
            });
        };
    }, [isCustomFilterDropdownOpen, setIsCustomFilterDropdownOpen]);

    useEffect(() => {
        const svgEl = document.querySelector("#product-type-filter .custom-filter-svg");
        if (svgEl) isCustomFilterDropdownOpen ? svgEl.classList.add("rotate-180") : svgEl.classList.remove("rotate-180");
    }, [isCustomFilterDropdownOpen]);

    if (showProductFilterOnCollection)
        return <div class="flex product-type-toggle-filter rounded-lg">
            {PRODUCT_TYPE_FILTERS.map(({ label, value }) => (
                <button key={label}
                    type="button"
                    disabled={value && !updatedTypeFilters?.some((filter) => filter.value === value)}
                    class={`py-1 px-7 rounded-lg duration-300 toggle-value ${previousAppliedFilter === value ? "selected-toggle" : ""}`}
                    onClick={() => handleProductTypeFilterUpdate(value)}>
                    {label}
                </button>
            ))}
        </div>;
};
